/**
 * Cookie utility functions and in-memory access token store.
 *
 * Security: The access token is stored in memory only (not in a cookie). This prevents
 * XSS from reading the token via document.cookie. Token lifetime is kept short;
 * MSAL refresh is used to obtain new tokens when needed.
 */
import {
  APP_JWT_ISSUER,
  APP_SESSION_STORAGE_KEY,
  TOKEN_STALE_THRESHOLD,
} from "../constants/auth";

/** In-memory access token (not in cookie to avoid XSS exposure). */
let inMemoryAccessToken: string | null = null;
/** Timestamp when the current token was set (for staleness checks). */
let tokenSetAt: number | null = null;

/** Read the `iss` claim from a JWT without verifying (best-effort). */
const _tokenIssuer = (token: string): string | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return (JSON.parse(json)?.iss as string) ?? null;
  } catch {
    return null;
  }
};

/**
 * Persist app-issued (email-code) tokens to sessionStorage so they survive a
 * page refresh — external users have no MSAL silent-refresh to repopulate the
 * in-memory token. MSAL tokens are never persisted. Cleared when the tab closes.
 */
const _persistIfAppToken = (token: string) => {
  try {
    if (_tokenIssuer(token) === APP_JWT_ISSUER) {
      sessionStorage.setItem(APP_SESSION_STORAGE_KEY, token);
    } else {
      sessionStorage.removeItem(APP_SESSION_STORAGE_KEY);
    }
  } catch {
    /* sessionStorage unavailable — degrade to in-memory only */
  }
};

// Rehydrate an app token persisted from a prior page load (survives refresh).
try {
  const persisted = sessionStorage.getItem(APP_SESSION_STORAGE_KEY);
  if (persisted) {
    inMemoryAccessToken = persisted;
    tokenSetAt = Date.now();
  }
} catch {
  /* sessionStorage unavailable */
}

/**
 * Set a cookie with the given name, value, and options
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {Object} options - Cookie options (expires, path, secure, etc.)
 */
export const setCookie = (
  name: string,
  value: string,
  options: Record<string, unknown> = {}
) => {
  const defaultOptions = {
    path: "/",
    secure: import.meta.env.PROD,
    sameSite: "strict",
    maxAge: 3600, // 1 hour in seconds
  };

  const cookieOptions = { ...defaultOptions, ...options } as {
    path: string;
    secure: boolean;
    sameSite: string;
    maxAge?: number;
  };

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (cookieOptions.maxAge) {
    cookieString += `; max-age=${cookieOptions.maxAge}`;
  }

  if (cookieOptions.path) {
    cookieString += `; path=${cookieOptions.path}`;
  }

  if (cookieOptions.secure) {
    cookieString += "; secure";
  }

  if (cookieOptions.sameSite) {
    cookieString += `; samesite=${cookieOptions.sameSite}`;
  }

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(`${name}=`)) {
      return decodeURIComponent(cookie.substring(name.length + 1));
    }
  }
  return null;
};

/**
 * Delete a cookie by name
 * @param {string} name - Cookie name
 */
export const deleteCookie = (name: string) => {
  document.cookie = `${name}=; max-age=0; path=/`;
};

/**
 * Set the access token in memory only (no cookie).
 * Keeps the token out of document.cookie so XSS cannot read it.
 * Short effective lifetime is enforced via TOKEN_STALE_THRESHOLD; MSAL refresh supplies new tokens.
 * @param {string} token - Access token (JWT)
 */
export const setAccessTokenCookie = (token: string) => {
  inMemoryAccessToken = token;
  tokenSetAt = Date.now();
  // Do not store access token in a cookie (httpOnly cannot be set from JS;
  // any client-accessible cookie is vulnerable to XSS).
  // App-issued (email-code) tokens are persisted to sessionStorage so they
  // survive a refresh; MSAL tokens are not (MSAL silent-refresh repopulates).
  _persistIfAppToken(token);
};

/**
 * Get the access token from memory.
 * @returns {string|null} - Access token or null if not found
 */
export const getAccessTokenCookie = (): string | null => {
  return inMemoryAccessToken;
};

/**
 * Check if the token needs refreshing (older than specified threshold)
 * @param {number} thresholdMinutes - Minutes threshold to consider token stale
 * @returns {boolean} - Whether token needs refreshing
 */
export const isTokenStale = (thresholdMinutes = TOKEN_STALE_THRESHOLD): boolean => {
  if (tokenSetAt == null) return true;
  const tokenAgeMinutes = (Date.now() - tokenSetAt) / (1000 * 60);
  return tokenAgeMinutes > thresholdMinutes;
};

/**
 * Check if an access token exists and is not expired
 * @returns {boolean} - Whether a valid token exists
 */
export const hasValidToken = (): boolean => {
  const token = getAccessTokenCookie();
  return !!token && !isTokenStale();
};

/**
 * Clear the in-memory access token (and any legacy cookies if present).
 */
export const deleteAccessTokenCookie = () => {
  inMemoryAccessToken = null;
  tokenSetAt = null;
  deleteCookie("access_token");
  deleteCookie("token_timestamp");
  try {
    sessionStorage.removeItem(APP_SESSION_STORAGE_KEY);
  } catch {
    /* sessionStorage unavailable */
  }
};
